/**
 * Идентификаторы Type2X.
 *
 * Каждый тип идентификатора живёт в собственном пространстве и не
 * взаимозаменяем с другими (docs/ARCHITECTURE.md §9.1, ТЗ SEC-01..SEC-14).
 * Брендирование на уровне типов не даёт случайно передать session_id туда,
 * где ожидается user_id.
 */

declare const brand: unique symbol;

/** Номинальный тип поверх string. */
type Branded<TName extends string> = string & { readonly [brand]: TName };

/** UUIDv7 пользователя. Публично неугадываем, email первичным ключом не является. */
export type UserId = Branded<'UserId'>;

/** Идентификатор гостевой установки (браузер + устройство). Не является личностью. */
export type InstallationId = Branded<'InstallationId'>;

/** Идентификатор тренировочной сессии. */
export type SessionId = Branded<'SessionId'>;

/** Клиентский идемпотентный ключ операции синхронизации. */
export type SyncId = Branded<'SyncId'>;

/** Идентификатор события платёжного провайдера. */
export type BillingEventId = Branded<'BillingEventId'>;

/** Идентификатор учебной единицы (клавиша, биграмма, слово, знак, шаблон). */
export type MasteryUnitId = Branded<'MasteryUnitId'>;

/** Идентификатор упражнения. */
export type ExerciseId = Branded<'ExerciseId'>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Проверяет, что строка похожа на UUID допустимой версии. */
export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

/**
 * Приводит строку к UserId после проверки формата.
 * @throws {TypeError} если значение не является UUID.
 */
export function toUserId(value: string): UserId {
  if (!isUuid(value)) throw new TypeError('UserId должен быть UUID');
  return value as UserId;
}

/** Приводит строку к InstallationId после проверки формата. */
export function toInstallationId(value: string): InstallationId {
  if (!isUuid(value)) throw new TypeError('InstallationId должен быть UUID');
  return value as InstallationId;
}

/** Приводит строку к SessionId после проверки формата. */
export function toSessionId(value: string): SessionId {
  if (!isUuid(value)) throw new TypeError('SessionId должен быть UUID');
  return value as SessionId;
}

/** Приводит строку к SyncId после проверки формата. */
export function toSyncId(value: string): SyncId {
  if (!isUuid(value)) throw new TypeError('SyncId должен быть UUID');
  return value as SyncId;
}

/** Приводит произвольную строку к MasteryUnitId (не UUID: это стабильный ключ материала). */
export function toMasteryUnitId(value: string): MasteryUnitId {
  if (value.length === 0) throw new TypeError('MasteryUnitId не может быть пустым');
  return value as MasteryUnitId;
}
